import React, { useState, useEffect } from 'react';
import { MessageSquare, MapPin } from 'lucide-react';
import { Customer } from '../../lib/customers';
import AddressAutocomplete from '../AddressAutocomplete';
import ModernMessageModal from '../ModernMessageModal';

interface BookingCustomerInfoProps {
  customer: Customer;
  onEdit?: () => void;
  bookingAddress?: string;
  onEditBookingAddress?: (address: string) => void;
  mobile_booking?: boolean;
  canEdit?: boolean;
}

export default function BookingCustomerInfo({ 
  customer, 
  onEdit,
  bookingAddress,
  onEditBookingAddress,
  mobile_booking = false,
  canEdit = true
}: BookingCustomerInfoProps) {
  const hasCustomerInfoAccess = true; // No longer using permissions
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [tempAddress, setTempAddress] = useState('');
  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);
  const [addressKey, setAddressKey] = useState(0); // Force re-render of AddressAutocomplete

  // Reset and re-initialize AddressAutocomplete when editing starts
  const handleStartEditing = () => {
    setTempAddress(bookingAddress || '');
    setIsEditingAddress(true);
    // Only force re-render when starting to edit, not during selection
    setAddressKey(prev => prev + 1);
  };

  if (!hasCustomerInfoAccess) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-3 py-2 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
        <h3 className="text-sm font-medium text-gray-900 flex items-center">
          Customer
          <button 
            className="ml-2 p-1 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
            onClick={() => setIsMessageModalOpen(true)}
            title="View message history"
          >
            <MessageSquare className="h-5 w-5" />
          </button>
        </h3>
        {canEdit && onEdit && (
          <button 
            onClick={onEdit}
            className="text-xs text-blue-600 hover:text-blue-800"
          >
            Edit
          </button>
        )}
      </div>
      <div className="p-2">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div className="flex items-center space-x-3 sm:space-x-4">
            <div className="h-8 w-8 rounded-full bg-black flex items-center justify-center text-white font-medium text-sm">
              {customer.first_name[0]}{customer.last_name[0]}
            </div>
            <div>
              <div className="text-sm font-medium text-gray-900">
                {customer.first_name} {customer.last_name}
              </div>
              <div className="text-xs text-gray-500">{customer.email}</div>
              {customer.phone && (
                <div className="text-xs text-gray-500">{customer.phone}</div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Booking Address Section - Only show for mobile bookings */}
      {mobile_booking && (
        <>
          <div className="px-3 py-2 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
            <h3 className="text-sm font-medium text-gray-900">Booking Address</h3>
            {canEdit && onEditBookingAddress && !isEditingAddress && (
              <button 
                onClick={handleStartEditing}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                Edit
              </button>
            )}
          </div>
          <div className="p-2">
            {isEditingAddress && onEditBookingAddress ? (
              <div className="space-y-2">
                <AddressAutocomplete
                  key={addressKey}
                  value={tempAddress}
                  onChange={(address) => setTempAddress(address)}
                  onSelect={(address) => {
                    console.log('BookingCustomerInfo: Address selected from Google Autocomplete:', address);
                    onEditBookingAddress(address.formatted);
                    setIsEditingAddress(false);
                    setTempAddress('');
                  }}
                  placeholder="Enter booking address"
                  className="w-full h-10 rounded-md border border-gray-300 shadow-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent px-3"
                />
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={() => {
                      setIsEditingAddress(false);
                      setTempAddress('');
                    }}
                    className="text-xs text-gray-600 hover:text-gray-800 px-2 py-1"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      if (tempAddress.trim()) {
                        console.log('BookingCustomerInfo: Saving manually entered address:', tempAddress.trim());
                        onEditBookingAddress(tempAddress.trim());
                      }
                      setIsEditingAddress(false);
                      setTempAddress('');
                    }}
                    className="text-xs text-blue-600 hover:text-blue-800 px-2 py-1"
                  >
                    Save
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <MapPin className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-600">
                  {bookingAddress || 'No booking address set'}
                </span>
              </div>
            )}
          </div>
        </>
      )}

      {/* Message Modal */}
      <ModernMessageModal
        isOpen={isMessageModalOpen}
        onClose={() => setIsMessageModalOpen(false)}
        customerId={customer.id}
        customerName={`${customer.first_name} ${customer.last_name}`}
        customerPhone={customer.phone}
        customerEmail={customer.email}
      />
    </div>
  );
}